/**
 * Утилиты для RTK Query пагинации
 * Предоставляют переиспользуемую логику для серializации, слияния и обновления данных
 */

export type PaginationRequest = {
	offset?: number | string;
	[key: string]: any;
};

export type PaginatedResponse<T, K extends string = 'orders'> = {
	[key in K]: T[];
} & Record<string, any>;

export type AggregateField = {
	name: string;
	isDecimal?: boolean;
};

/**
 * Создаёт конфигурацию пагинации для RTK Query endpoints
 * @param dataKey - ключ массива данных в ответе (например, 'orders', 'activities')
 * @param aggregateFields - поля агрегатов для суммирования (например, ['count_orders', 'sum_points'])
 * @returns объект с serializeQueryArgs, merge и forceRefetch
 * 
 * @example
 * // В RTK Query endpoint:
 * ordersList: builder.query<ResponseType, RequestType>({
 *   query: (data) => ({
 *     func: orderAPI.getUserOrders,
 *     args: [data],
 *   }),
 *   ...createPaginationConfig('orders', [
 *     { name: 'count_orders', isDecimal: false },
 *     { name: 'sum_points', isDecimal: true },
 *     { name: 'sum_money', isDecimal: true }
 *   ]),
 *   providesTags: ['orders-list'],
 * }),
 */
export const createPaginationConfig = <
	TRequest extends PaginationRequest = PaginationRequest,
	TResponse extends Record<string, any> = any,
>(
	dataKey: string = 'orders',
	aggregateFields: AggregateField[] = [],
) => ({
	/**
	 * Сериализация аргументов запроса для кэширования
	 * Исключает offset из ключа кэша, чтобы запросы с одинаковыми фильтрами объединялись
	 */
	serializeQueryArgs: ({
		endpointName,
		queryArgs,
	}: {
		endpointName: string;
		queryArgs?: TRequest;
	}) => {
		const {offset, ...filters} = (queryArgs || {}) as TRequest;
		return `${endpointName}:${JSON.stringify(filters)}`;
	},

	/**
	 * Слияние данных при пагинации
	 * Если offset = 0, заменяем данные полностью (новый поиск/фильтр)
	 * Если offset > 0, добавляем новые данные к существующим
	 */
	merge: (
		currentCache: TResponse,
		newItems: TResponse,
		{arg}: {arg?: TRequest},
	) => {
		// Если offset = 0, заменяем данные полностью (новый поиск/фильтр)
		if (arg?.offset === 0) {
			return newItems;
		}

		// Если offset > 0, добавляем новые данные к существующим
		const prevData = (currentCache[dataKey as keyof TResponse] || []) as any[];
		const newData = (newItems[dataKey as keyof TResponse] || []) as any[];

		// Добавляем новые данные в конец
		(currentCache as any)[dataKey] = [...prevData, ...newData];

		// Суммируем агрегаты
		if (aggregateFields.length > 0) {
			mergeAggregates(currentCache, newItems, aggregateFields);
		}

		return currentCache;
	},

	/**
	 * Принудительное обновление данных при изменении параметров
	 */
	forceRefetch: ({
		currentArg,
		previousArg,
	}: {
		currentArg?: TRequest;
		previousArg?: TRequest;
	}) => {
		return (
			currentArg?.offset !== previousArg?.offset ||
			JSON.stringify(currentArg) !== JSON.stringify(previousArg)
		);
	},
});

/**
 * Слияние агрегатных полей (count, sum и т.д.)
 * @param cache - текущий кэш
 * @param newData - новые данные
 * @param fields - массив полей агрегатов для суммирования
 */
export const mergeAggregates = <T extends Record<string, any>>(
	cache: T,
	newData: T,
	fields: AggregateField[],
): void => {
	fields.forEach(({name, isDecimal = true}) => {
		const prevValue = Number(cache[name]) || 0;
		const newValue = Number(newData[name]) || 0;
		const sumValue = prevValue + newValue;

		(cache as any)[name] = isDecimal ? sumValue.toFixed(2) : sumValue;
	});
};

/**
 * Создаёт конфигурацию пагинации с уникальными элементами по ID
 * Полезно, когда могут приходить дубликаты данных
 * @param dataKey - ключ массива данных в ответе
 * @param aggregateFields - поля агрегатов для суммирования
 * @param idKey - ключ уникального идентификатора (по умолчанию 'id')
 * @returns объект с serializeQueryArgs, merge и forceRefetch
 */
export const createUniquePaginationConfig = <
	TRequest extends PaginationRequest = PaginationRequest,
	TResponse extends Record<string, any> = any,
	TItem extends {id?: string | number} = any,
>(
	dataKey: string = 'orders',
	aggregateFields: AggregateField[] = [],
	idKey: keyof TItem = 'id',
) => ({
	serializeQueryArgs: ({
		endpointName,
		queryArgs,
	}: {
		endpointName: string;
		queryArgs?: TRequest;
	}) => {
		const {offset, ...filters} = (queryArgs || {}) as TRequest;
		return `${endpointName}:${JSON.stringify(filters)}`;
	},

	merge: (
		currentCache: TResponse,
		newItems: TResponse,
		{arg}: {arg?: TRequest},
	) => {
		// Если offset = 0, заменяем данные полностью
		if (arg?.offset === 0) {
			return newItems;
		}

		const prevData = (currentCache[dataKey as keyof TResponse] || []) as TItem[];
		const newData = (newItems[dataKey as keyof TResponse] || []) as TItem[];

		// Объединяем уникально по ID
		const dataMap = new Map<string | number, TItem>();
		prevData.forEach((item) => {
			const id = item[idKey];
			if (id !== undefined && id !== null) {
				dataMap.set(id as string | number, item);
			}
		});
		newData.forEach((item) => {
			const id = item[idKey];
			if (id !== undefined && id !== null) {
				dataMap.set(id as string | number, item);
			}
		});

		(currentCache as any)[dataKey] = Array.from(dataMap.values());

		// Суммируем агрегаты
		if (aggregateFields.length > 0) {
			mergeAggregates(currentCache, newItems, aggregateFields);
		}

		return currentCache;
	},

	forceRefetch: ({
		currentArg,
		previousArg,
	}: {
		currentArg?: TRequest;
		previousArg?: TRequest;
	}) => {
		return (
			currentArg?.offset !== previousArg?.offset ||
			JSON.stringify(currentArg) !== JSON.stringify(previousArg)
		);
	},
});

